// installFeedbackModule.js — installer con adapter. axios/router iniettati; nessun
// import obbligatorio da @/router o @/axiosInstance; nessun path host hardcoded.
import FeedbackWidget from "./components/FeedbackWidget.vue";
import { initClientLogCapture } from "./services/clientLogService";
import {
  _setApiClient,
  _setObservabilityProvider,
  _setTracker,
} from "./services/feedbackService";
import {
  makeFeedbackPanelRoute,
  feedbackPanelRoute,
  feedbackAdminRoute,
} from "./routes";

export function installFeedbackModule({
  app,
  router = null,
  axiosInstance,
  isAuthenticated = () => true, // widget visibile ai loggati
  canAccessPanel = null, // default: GET /feedback/access/me
  panelParentRouteName = null, // dove agganciare la route (router.addRoute)
  panelPath = "/feedback", // configurabile (niente /home hardcoded)
  notify = null, // default: $toast → console
  accessDeniedRedirect = "/", // dove mandare chi non ha accesso al pannello
  onAccessDenied = null, // hook custom: onAccessDenied({ next, router })
  // Osservabilità [sc-47909] — entrambi OPZIONALI e iniettati dall'host, così il
  // modulo resta portabile (nessun import di utility del CRM).
  getObservability = null, // () => { lt_correlation_id, appId, release, glitchtipEventId, umamiWebsiteId }
  track = null, // (eventName, data) => void — analytics (es. Umami)
} = {}) {
  if (!app) throw new Error("[feedback] app richiesta");
  if (!axiosInstance) throw new Error("[feedback] axiosInstance richiesta");
  _setApiClient(axiosInstance);
  _setObservabilityProvider(getObservability);
  _setTracker(track);

  const resolvedCanAccessPanel =
    typeof canAccessPanel === "function"
      ? canAccessPanel
      : async () => {
          try {
            const { data } = await axiosInstance.get("/feedback/access/me");
            return data?.canAccessPanel === true;
          } catch {
            return false;
          }
        };

  const defaultNotify = (msg, opts = {}) => {
    const t = app?.config?.globalProperties?.$toast;
    if (t) {
      const type = opts.type || "info";
      (t[type] || t.info || t.open || (() => {})).call(t, msg, opts);
    } else console.log("[feedback]", msg);
  };

  const cfg = {
    axiosInstance,
    router,
    isAuthenticated,
    canAccessPanel: resolvedCanAccessPanel,
    notify: typeof notify === "function" ? notify : defaultNotify,
    track: typeof track === "function" ? track : () => {},
    panelPath: String(panelPath).startsWith("/")
      ? String(panelPath)
      : "/" + String(panelPath),
  };

  // Route pannello con beforeEnter interno (indipendente dalle guard del CRM host).
  if (router) {
    const beforeEnter = async (_to, _from, next) => {
      let allowed = false;
      try {
        allowed = (await resolvedCanAccessPanel()) === true;
      } catch {
        allowed = false;
      }
      if (allowed) return next();
      cfg.notify("Accesso al Pannello Assistenza non consentito.", {
        type: "error",
      });
      // Redirect configurabile: nessun path host hardcoded.
      if (typeof onAccessDenied === "function")
        return onAccessDenied({ next, router });
      return next({ path: accessDeniedRedirect });
    };
    const childPath = String(panelPath).replace(/^\//, "");
    const route = makeFeedbackPanelRoute({ path: childPath, beforeEnter });
    if (panelParentRouteName) router.addRoute(panelParentRouteName, route);
    else router.addRoute({ ...route, path: "/" + childPath });
    // Path completo risolto (gestisce il nesting sotto il parent) per la navigazione del widget.
    try {
      cfg.panelPath = router.resolve({ name: "feedback-panel" }).path;
    } catch {
      /* mantiene default */
    }
  }

  app.provide("feedbackConfig", cfg);
  app.component("FeedbackWidget", FeedbackWidget);
  initClientLogCapture();

  return { feedbackPanelRoute, feedbackAdminRoute, panelPath: cfg.panelPath };
}

export {
  FeedbackWidget,
  initClientLogCapture,
  feedbackPanelRoute,
  feedbackAdminRoute,
};
export default installFeedbackModule;
